import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// NOTA: Preescolar y Secundaria ocultos temporalmente — solo Primaria activa.
const NIVELES = ["PRIMARIA"];

export default function Zonas() {
  const [items, setItems] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [supervisores, setSupervisores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ numero: "", nombre: "", sector_id: "", supervisor_user_id: "", nivel: "" });
  const { user } = useAuth();
  const readOnly = ["jefe_sector", "supervisor_zona"].includes(user?.role);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const [z, s, sup] = await Promise.all([
        api.get("/zonas"),
        api.get("/sectores"),
        api.get("/usuarios", { params: { role: "supervisor_zona" } }),
      ]);
      // Solo Primaria activa por ahora
      const zonasPrim = (z.data || []).filter((zn) => !zn.nivel || zn.nivel === "PRIMARIA");
      setItems(zonasPrim); setSectores(s.data); setSupervisores(sup.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ numero: "", nombre: "", sector_id: "", supervisor_user_id: "", nivel: "" }); setOpen(true); };
  const startEdit = (z) => {
    setEditing(z);
    setForm({
      numero: z.numero, nombre: z.nombre || "",
      sector_id: z.sector_id || "", supervisor_user_id: z.supervisor_user_id || "",
      nivel: z.nivel || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        sector_id: form.sector_id || null,
        supervisor_user_id: form.supervisor_user_id || null,
        nivel: form.nivel || null,
      };
      if (editing) await api.put(`/zonas/${editing.id}`, payload);
      else await api.post("/zonas", payload);
      toast.success(editing ? "Zona actualizada" : "Zona creada");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/zonas/${confirm.id}`);
      toast.success("Zona eliminada"); setConfirm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Administración</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Zonas escolares</h1>
          </div>
          {!readOnly && (
            <Button data-testid="btn-add-zona" onClick={startCreate} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nueva zona
            </Button>
          )}
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>Número</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Nivel</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>Supervisor</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10">
                  <MapPin className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                  <div className="text-sm text-[#545454]">Sin zonas registradas</div>
                </TableCell></TableRow>
              ) : items.map((z) => (
                <TableRow key={z.id} data-testid={`zona-row-${z.id}`}>
                  <TableCell className="font-mono">{z.numero}</TableCell>
                  <TableCell>{z.nombre || "—"}</TableCell>
                  <TableCell>{z.nivel || "—"}</TableCell>
                  <TableCell>{z.sector ? `${z.sector.numero} ${z.sector.nombre || ""}` : "—"}</TableCell>
                  <TableCell>{z.supervisor?.name || z.supervisor?.email || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(z)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirm(z)}><Trash2 className="w-4 h-4 text-[#9F2241]" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar zona" : "Nueva zona escolar"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Número *</Label>
                <Input data-testid="zona-numero" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value.toUpperCase() })} required /></div>
              <div><Label className="text-xs">Nivel</Label>
                <Select value={form.nivel || "__none__"} onValueChange={(v) => setForm({ ...form, nivel: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="zona-nivel"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin especificar —</SelectItem>
                    {NIVELES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Nombre</Label>
              <Input data-testid="zona-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} /></div>
            <div><Label className="text-xs">Sector</Label>
              <Select value={form.sector_id || "__none__"} onValueChange={(v) => setForm({ ...form, sector_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="zona-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin asignar —</SelectItem>
                  {sectores.map((s) => <SelectItem key={s.id} value={s.id}>{s.numero} {s.nombre}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Supervisor</Label>
              <Select value={form.supervisor_user_id || "__none__"} onValueChange={(v) => setForm({ ...form, supervisor_user_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="zona-supervisor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin asignar —</SelectItem>
                  {supervisores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-zona" type="submit" className="bg-[#235B4E] hover:bg-[#1D4A40]">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar zona?</AlertDialogTitle>
            <AlertDialogDescription>Se desvincularán planteles y supervisores asociados.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
